import { Link } from '@inertiajs/react'
import LogoRail from '@/components/marketing/LogoRail'
import ScrollReveal from '@/components/marketing/ScrollReveal'
import FaqAccordion from '@/components/marketing/FaqAccordion'
import type { SharedMarketingProps } from '@/types/marketing'

interface ArtistsPageProps extends SharedMarketingProps {
  hero: {
    eyebrow: string
    title: string
    body: string
    bg_image?: string
    metrics: { value: string; label: string }[]
    steps: { label: string; title: string; copy: string }[]
    primary_cta: { label: string; href: string }
    secondary_cta: { label: string; href: string }
  }
  programs: {
    tag: string
    title: string
    logo: string
    description: string
    bullets: string[]
    cta: { label: string; href: string | null }
  }[]
  highlights: {
    title: string
    body: string
    bullets: string[]
  }[]
  platform_intro: { eyebrow: string; title: string; body: string }
  platform_logos: { name: string; src: string }[]
  faq_items: { question: string; answer: string }[]
  final_cta: {
    title: string
    body: string
    actions: { label: string; href: string }[]
  }
}

export default function Artists(props: ArtistsPageProps) {
  return (
    <>
      {/* Hero — same v2 layered treatment as Home/India/Thailand. */}
      <section className="marketing-hero-section hero-v2 relative overflow-hidden" style={{ minHeight: '62vh' }}>
        {props.hero.bg_image && (
          <div className="hero-bg" style={{ backgroundImage: `url(${props.hero.bg_image})` }} />
        )}
        <div className="hero-fade" aria-hidden="true" />
        <div className="hero-spot-warm" aria-hidden="true" />
        <div className="hero-spot-cool" aria-hidden="true" />
        <div className="hero-bottom-fade" aria-hidden="true" />

        <div className="hero-content container relative z-10 mx-auto px-6 pb-8 pt-10 lg:pb-10 lg:pt-12">
          <div className="hero-text">
            <span className="hero-eyebrow">{props.hero.eyebrow}</span>

            <h1 className="hero-headline" style={{ fontSize: 'clamp(2.5rem, 7vw, 6rem)' }}>{props.hero.title}</h1>

            <p className="hero-body">{props.hero.body}</p>

            <div className="hero-ctas">
              <a href={props.hero.primary_cta.href} className="marketing-button-primary">
                {props.hero.primary_cta.label}
              </a>
              <Link href={props.hero.secondary_cta.href} className="marketing-button-secondary" prefetch>
                {props.hero.secondary_cta.label}
              </Link>
            </div>
          </div>

          <div className="hero-stats">
            <div className="hero-stat-grid">
              {props.hero.metrics.map((metric) => (
                <div key={metric.label} className="hero-stat-card">
                  <span className="hero-stat-value">{metric.value}</span>
                  <span className="hero-stat-label">{metric.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Merged dark info zone — Steps (3-up) + Highlights (2-up) stack
          in one <section> so the page doesn't have two consecutive dark
          slabs after the hero. */}
      <section className="marketing-section has-glow">
        <div className="section-glow warm" style={{ width: '38rem', height: '38rem', right: '-10rem', bottom: '-14rem' }} />
        <div className="container mx-auto px-6 space-y-16">
          <div>
            <ScrollReveal direction="up">
              <div className="marketing-section-header">
                <div className="marketing-eyebrow-mono">Your options</div>
                <h2 className="marketing-section-title">Three ways to release with wavbee.</h2>
              </div>
            </ScrollReveal>
            <div className="grid gap-5 lg:grid-cols-3">
              {props.hero.steps.map((step, index) => (
                <ScrollReveal key={step.label} direction="up" delay={index * 0.1}>
                  <div className="marketing-surface-card h-full">
                    <div className="marketing-eyebrow-mono">{step.label}</div>
                    <h3 className="mt-2 text-xl font-semibold text-white">{step.title}</h3>
                    <p className="mt-3 text-sm leading-7 text-white/72">{step.copy}</p>
                  </div>
                </ScrollReveal>
              ))}
            </div>
          </div>

          <div>
            <ScrollReveal direction="up">
              <div className="marketing-section-header">
                <div className="marketing-eyebrow-mono">What you get</div>
                <h2 className="marketing-section-title">Real distribution. Real support.</h2>
              </div>
            </ScrollReveal>
            <div className="grid gap-6 lg:grid-cols-2">
              {props.highlights.map((item, index) => (
                <ScrollReveal key={item.title} direction={index === 0 ? 'left' : 'right'}>
                  <div className="marketing-surface-card h-full">
                    <h3 className="text-3xl font-semibold text-white">{item.title}</h3>
                    <p className="mt-4 text-sm leading-7 text-white/72">{item.body}</p>
                    <ul className="mt-6 space-y-3 text-sm text-white/82">
                      {item.bullets.map((bullet) => (
                        <li key={bullet} className="marketing-bullet-item">{bullet}</li>
                      ))}
                    </ul>
                  </div>
                </ScrollReveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Programs — cream slab (already cream, kept). */}
      <section className="marketing-section marketing-section-warm">
        <div className="container mx-auto px-6">
          <ScrollReveal direction="up">
            <div className="marketing-section-header">
              <div className="marketing-eyebrow-mono">Choose your program</div>
              <h2 className="marketing-section-title marketing-section-title-dark">Three programs to match where you are in your music career.</h2>
            </div>
          </ScrollReveal>
          <div className="grid gap-6 lg:grid-cols-3">
            {props.programs.map((program, index) => (
              <ScrollReveal key={program.title} direction="up" delay={index * 0.1}>
                <div className="marketing-light-card h-full">
                  <div className="flex h-28 items-center justify-end lg:justify-start">
                    <img src={program.logo} alt={program.title} className="max-h-24 w-auto object-contain" />
                  </div>
                  <div className="marketing-eyebrow-mono mt-4">{program.tag}</div>
                  <p className="mt-4 text-sm leading-7 text-[#3b3b3b]">{program.description}</p>
                  <ul className="mt-6 space-y-3 text-sm text-[#202020]">
                    {program.bullets.map((bullet) => (
                      <li key={bullet} className="marketing-bullet-item marketing-bullet-item-dark">{bullet}</li>
                    ))}
                  </ul>
                  <div className="mt-7">
                    {program.cta.href ? (
                      <a href={program.cta.href} className="marketing-button-primary">
                        {program.cta.label}
                      </a>
                    ) : (
                      <span className="marketing-button-disabled">
                        {program.cta.label}
                      </span>
                    )}
                  </div>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Platforms — dark, with See all stores button matching Home. */}
      <section className="marketing-section">
        <div className="container mx-auto px-6">
          <ScrollReveal direction="up">
            <div className="marketing-section-header">
              <div className="marketing-eyebrow-mono">{props.platform_intro.eyebrow}</div>
              <h2 className="marketing-section-title">{props.platform_intro.title}</h2>
              <p className="marketing-lead max-w-3xl">{props.platform_intro.body}</p>
            </div>
          </ScrollReveal>
          <ScrollReveal direction="fade">
            <LogoRail items={props.platform_logos} />
          </ScrollReveal>
          <ScrollReveal delay={0.1}>
            <div className="mt-8 flex justify-center">
              <Link href="/stores" className="marketing-button-secondary" prefetch>
                See all stores →
              </Link>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* FAQ — cream so the tone alternates against the dark platform section
          before it and the dark final CTA after it. */}
      <section id="faq" className="marketing-section marketing-section-warm">
        <div className="container mx-auto px-6">
          <ScrollReveal direction="up">
            <div className="marketing-section-header">
              <div className="marketing-eyebrow-mono">FAQ</div>
              <h2 className="marketing-section-title marketing-section-title-dark">Artist questions</h2>
            </div>
          </ScrollReveal>
          <ScrollReveal direction="up" delay={0.1}>
            <FaqAccordion items={props.faq_items} />
          </ScrollReveal>
          <ScrollReveal direction="up" delay={0.15}>
            <div className="mt-8 text-center">
              <Link href="/help" className="text-sm font-medium text-[#0f0f0f]/60 hover:text-[#ff6a1a] transition-colors" prefetch>
                See all FAQs →
              </Link>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Final CTA — dark now that the cream FAQ sits before it. */}
      <section className="marketing-section">
        <div className="container mx-auto px-6">
          <ScrollReveal direction="up">
            <div className="marketing-final-cta">
              <div>
                <div className="marketing-eyebrow-mono">Ready when you are</div>
                <h2 className="marketing-section-title">{props.final_cta.title}</h2>
                <p className="marketing-lead max-w-2xl">{props.final_cta.body}</p>
              </div>
              <div className="mt-8 flex flex-wrap gap-3">
                {props.final_cta.actions.map((action, index) => {
                  const className = index === 0 ? 'marketing-button-primary' : 'marketing-button-secondary'
                  return action.href.includes('/apply') ? (
                    <a key={action.href} href={action.href} className={className}>
                      {action.label}
                    </a>
                  ) : (
                    <Link key={action.href} href={action.href} className={className} prefetch>
                      {action.label}
                    </Link>
                  )
                })}
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>
    </>
  )
}
